import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError, shareReplay } from 'rxjs/operators';

import { ApiService } from './api.service';

export interface SlugResolutionResponse {
  channel_id?: string;
  default_destination: string;
  [key: string]: any;
}

export interface SlugExistsResponse {
  slug: string;
  exists: boolean;
}

export interface ChannelBrands {
  iconLogo?: string;
  footerLogo?: string;
  headerLogo?: string;
}

export interface ChannelMenus {
  indique?: boolean;
  parceiros?: boolean;
  [key: string]: boolean | undefined;
}

export interface ChannelMovelProduct {
  uuid: string;
  image: string;
  title: string;
  description: string;
  link: string;
  price: number;
  channelSlug?: string;
  channelUuid?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ChannelMovelProductsResponse {
  content: ChannelMovelProduct[];
  totalElements?: number;
  totalPages?: number;
  last?: boolean;
  size?: number;
  number?: number;
  numberOfElements?: number;
  first?: boolean;
  empty?: boolean;
}

export interface ChannelData {
  uuid: string;
  name: string;
  ownerUuid: string;
  status: string;
  slug: string;
  defaultDestination: string;
  utmSource: string;
  utmMedium: string;
  utmCampaing: string;
  mark: string | null;
  colors: any;
  offersUuid: string[];
  categoriesUuid: string[];
  notes: string;
  createdAt: string;
  updatedAt: string;
  banners: string;
  menus: string;
  brands: string;
  offersMindId: any;
  catalogs: string[];
  homeHeadline?: string | null;
  homeSubheadline?: string | null;
  mindImageUrls?: string[] | null;
  addressModalSubheadline?: string | null;
  referralHeadline?: string | null;
  referralSubheadline?: string | null;
  referralRegulationUrl?: string | null;
  referralButtonLabel?: string | null;
  referralBannerUrl?: string | null;
  referralBannerMobileUrl?: string | null;
  /** Affiliate id for Mind createOrder — from channel API (`/umanager/channels/slug/{slug}`). */
  affiliateId?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class UManagerSlugService {
  private readonly API_URL = '/external/umanager/slug';
  private readonly CHANNELS_API_URL = '/umanager/channels';

  private readonly slugExistsCache = new Map<string, Observable<SlugExistsResponse | null>>();
  private readonly channelBySlugCache = new Map<string, Observable<ChannelData | null>>();
  private apiService = inject(ApiService);


  resolveSlug(slug: string): Observable<SlugResolutionResponse | null> {
    if (!slug) {
      return of(null);
    }

    return this.apiService.get<SlugResolutionResponse>(`${this.API_URL}/${slug}`).pipe(
      map(response => response || null),
      catchError(error => {
        console.error('Erro ao resolver slug via uManager:', error);
        return of(null);
      })
    );
  }

  checkSlugExists(slug: string): Observable<SlugExistsResponse | null> {
    const key = (slug || '').trim();
    if (!key) {
      return of(null);
    }

    const cached = this.slugExistsCache.get(key);
    if (cached) return cached;

    const request$ = this.apiService.get<SlugExistsResponse>(`${this.CHANNELS_API_URL}/slug/${key}/exists`).pipe(
      map(response => response || null),
      catchError(error => {
        console.error('Erro ao verificar se slug existe:', error);
        return of(null);
      }),
      // Important: share result across multiple subscribers/components; avoid duplicate HTTP calls.
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.slugExistsCache.set(key, request$);
    return request$;
  }

  getChannelBySlug(slug: string): Observable<ChannelData | null> {
    const key = (slug || '').trim();
    if (!key) {
      return of(null);
    }

    const cached = this.channelBySlugCache.get(key);
    if (cached) return cached;

    const request$ = this.apiService.get<ChannelData>(`${this.CHANNELS_API_URL}/slug/${key}`).pipe(
      map(response => response || null),
      catchError(error => {
        console.error('Erro ao buscar dados do canal:', error);
        return of(null);
      }),
      // Important: share result across multiple subscribers/components; avoid duplicate HTTP calls.
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.channelBySlugCache.set(key, request$);
    return request$;
  }

  getChannelProducts(slug: string): Observable<ChannelMovelProduct[]> {
    const key = (slug || '').trim();
    if (!key) {
      return of([]);
    }

    return this.apiService.get<ChannelMovelProductsResponse | ChannelMovelProduct[]>(
      `${this.CHANNELS_API_URL}/${key}/products`
    ).pipe(
      map(response => {
        if (Array.isArray(response)) {
          return response;
        }
        return response?.content ?? [];
      }),
      catchError(error => {
        console.error('Erro ao buscar produtos móveis do canal:', error);
        throw error;
      })
    );
  }
}

